import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderApi, OrderResponse } from '../services/orderApi';

export interface Product {
  id: string;
  title: string;
  type: string;
  location: string;
  price: number;
  images: string[];
  bedrooms: number;
  bathrooms: number;
  area: number;
  payment_term?: string;
  min_duration?: string;
  owner: {
    name: string;
    avatar: string;
    verified: boolean;
  };
}

export interface OrderItem {
  product: Product;
  duration: number;
  startDate: Date;
  endDate: Date;
  totalPrice: number;
  deposit: number;
  commission: number;
}

export interface PaymentMethod {
  id: string;
  type: 'bank' | 'card' | 'ewallet' | 'qr' | 'wallet' | 'cip';
  name: string;
  icon: string;
  description: string;
  isAvailable: boolean;
}

export interface Order {
  id: string;
  items: OrderItem[];
  customer: {
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  paymentMethod: PaymentMethod | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  createdAt: Date;
  updatedAt: Date;
  totalAmount: number;
  depositAmount: number;
  commissionAmount: number;
}

interface OrderContextType {
  currentOrder: Order | null;
  orderHistory: Order[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  createOrder: (product: Product, duration: number, startDate: Date) => Promise<string>;
  loadOrder: (orderId: string) => Promise<void>;
  updateOrder: (orderId: string, updates: Partial<Order>) => Promise<void>;
  cancelOrder: (orderId: string) => Promise<void>;
  selectPaymentMethod: (paymentMethod: PaymentMethod) => void;
  processPayment: (orderId: string) => Promise<boolean>;
  clearCurrentOrder: () => void;
  clearError: () => void;
}

const OrderContext = createContext<OrderContextType | null>(null);

// Helper function to convert OrderResponse from API to Order interface
const convertOrderResponseToOrder = (apiOrder: OrderResponse): Order => {
  const property = apiOrder.property;
  
  // Map payment method string to PaymentMethod object
  const mapPaymentMethod = (method?: string): PaymentMethod | null => {
    if (!method) return null;
    
    const methodMap: Record<string, PaymentMethod> = {
      'internal_wallet': {
        id: 'internal_wallet',
        type: 'wallet',
        name: 'Ví Nội Bộ',
        icon: 'fas fa-wallet',
        description: 'Thanh toán từ số dư ví trong tài khoản của bạn',
        isAvailable: true,
      },
      'wallet': {
        id: 'wallet',
        type: 'wallet',
        name: 'Ví Nội Bộ',
        icon: 'fas fa-wallet',
        description: 'Thanh toán từ số dư ví trong tài khoản của bạn',
        isAvailable: true,
      },
      'bank_transfer': {
        id: 'bank_transfer',
        type: 'bank',
        name: 'Chuyển khoản ngân hàng',
        icon: 'fas fa-university',
        description: 'Chuyển khoản trực tiếp đến tài khoản ngân hàng',
        isAvailable: true,
      },
      'credit_card': {
        id: 'credit_card',
        type: 'card',
        name: 'Thẻ tín dụng/ghi nợ',
        icon: 'fas fa-credit-card',
        description: 'Thanh toán bằng thẻ Visa, Mastercard, JCB',
        isAvailable: true,
      },
      'ewallet': {
        id: 'ewallet',
        type: 'ewallet',
        name: 'Ví điện tử',
        icon: 'fas fa-mobile-alt',
        description: 'Thanh toán qua VNPay, MoMo, ZaloPay',
        isAvailable: true,
      },
      'momo': {
        id: 'ewallet',
        type: 'ewallet',
        name: 'Ví điện tử',
        icon: 'fas fa-mobile-alt',
        description: 'Thanh toán qua VNPay, MoMo, ZaloPay',
        isAvailable: true,
      },
      'vnpay': {
        id: 'ewallet',
        type: 'ewallet',
        name: 'Ví điện tử',
        icon: 'fas fa-mobile-alt',
        description: 'Thanh toán qua VNPay, MoMo, ZaloPay',
        isAvailable: true,
      },
      'zalopay': {
        id: 'ewallet',
        type: 'ewallet',
        name: 'Ví điện tử',
        icon: 'fas fa-mobile-alt',
        description: 'Thanh toán qua VNPay, MoMo, ZaloPay',
        isAvailable: true,
      },
      'cip': {
        id: 'cip',
        type: 'cip',
        name: 'Thanh toán bằng CIP',
        icon: 'fas fa-barcode',
        description: 'Thanh toán an toàn qua hệ thống CIP',
        isAvailable: true,
      },
      'qr_code': {
        id: 'qr_code',
        type: 'qr',
        name: 'Quét mã QR',
        icon: 'fas fa-qrcode',
        description: 'Quét mã QR để thanh toán',
        isAvailable: true,
      },
    };
    
    return methodMap[method] || null;
  };

  // Map status string to Order status type
  const mapStatus = (status: string): Order['status'] => {
    const statusMap: Record<string, Order['status']> = {
      'pending': 'pending',
      'processing': 'processing',
      'completed': 'completed',
      'failed': 'failed',
      'cancelled': 'cancelled',
    };
    return statusMap[status.toLowerCase()] || 'pending';
  };

  const product: Product = property ? {
    id: String(property.id),
    title: property.title,
    type: property.type,
    location: property.location,
    price: property.price,
    images: property.images || [],
    bedrooms: property.bedrooms || 0,
    bathrooms: property.bathrooms || 0,
    area: property.area || 0,
    payment_term: property.payment_term,
    min_duration: property.min_duration,
    owner: property.owner ? {
      name: property.owner.name,
      avatar: property.owner.avatar || '',
      verified: property.owner.verified,
    } : {
      name: 'Chủ nhà',
      avatar: '',
      verified: false,
    },
  } : {
    id: String(apiOrder.property_id),
    title: 'N/A',
    type: 'N/A',
    location: 'N/A',
    price: apiOrder.monthly_price,
    images: [],
    bedrooms: 0,
    bathrooms: 0,
    area: 0,
    payment_term: undefined,
    owner: {
      name: 'Chủ nhà',
      avatar: '',
      verified: false,
    },
  };

  return {
    id: String(apiOrder.id),
    items: [{
      product,
      duration: apiOrder.duration_months,
      startDate: new Date(apiOrder.start_date),
      endDate: new Date(apiOrder.end_date),
      totalPrice: apiOrder.total_rental_amount,
      deposit: apiOrder.deposit_amount,
      commission: apiOrder.commission_amount,
    }],
    customer: {
      name: apiOrder.customer_name || '',
      email: apiOrder.customer_email || '',
      phone: apiOrder.customer_phone || '',
      address: apiOrder.customer_address || '',
    },
    paymentMethod: mapPaymentMethod(apiOrder.payment_method),
    status: mapStatus(apiOrder.status),
    createdAt: new Date(apiOrder.created_at),
    updatedAt: new Date(apiOrder.updated_at),
    totalAmount: apiOrder.total_amount,
    depositAmount: apiOrder.deposit_amount,
    commissionAmount: apiOrder.commission_amount,
  };
};

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [orderHistory, setOrderHistory] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Note: Payment methods are configured per order via API

  const loadOrder = useCallback(async (orderId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if user is authenticated before making API call
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        const errorMessage = 'Vui lòng đăng nhập để xem đơn hàng.';
        setError(errorMessage);
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      // Call API to get order
      const apiOrder = await orderApi.getOrder(orderId);
      const order = convertOrderResponseToOrder(apiOrder);
      setCurrentOrder(order);
    } catch (err: any) {
      // Handle authentication errors specifically
      if (err.message?.includes('No authentication token') || err.message?.includes('authentication')) {
        const errorMessage = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
        setError(errorMessage);
        // Redirect to login
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const errorMessage = err.message || 'Không thể tải đơn hàng. Vui lòng thử lại.';
        setError(errorMessage);
        console.error('Error loading order:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createOrder = useCallback(async (
    product: Product, 
    duration: number, 
    startDate: Date
  ): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);

      // Calculate end date
      const endDate = new Date(startDate);
      console.log(`📋 [CREATE-ORDER] Before setMonth:`, {
        startDate: startDate.toISOString(),
        startDateMonth: startDate.getMonth(),
        endDate: endDate.toISOString(),
        endDateMonth: endDate.getMonth(),
        duration,
      });
      
      endDate.setMonth(endDate.getMonth() + duration);

      console.log(`📋 [CREATE-ORDER] After setMonth:`, {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      // Format dates as ISO strings
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      console.log(`📋 [CREATE-ORDER] Formatted dates:`, {
        startDateStr,
        endDateStr,
        duration,
      });

      // Call API to create order
      const apiOrder = await orderApi.createOrder({
        property_id: parseInt(product.id),
        duration_months: duration,
        start_date: startDateStr,
        end_date: endDateStr,
        customer_name: '', // Will be filled in payment page
        customer_email: undefined,
        customer_phone: undefined,
        customer_address: undefined,
      });

      // Convert API response to Order
      const newOrder = convertOrderResponseToOrder(apiOrder);
      setCurrentOrder(newOrder);
      
      // Navigate to payment page
      navigate(`/payment/${newOrder.id}`);
      
      return newOrder.id;
    } catch (err: any) {
      const errorMessage = err.message || 'Không thể tạo đơn hàng. Vui lòng thử lại.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const updateOrder = useCallback(async (orderId: string, updates: Partial<Order>) => {
    try {
      setIsLoading(true);
      setError(null);

      // Prepare API update request
      const apiUpdates: any = {};

      if (updates.customer) {
        apiUpdates.customer_name = updates.customer.name;
        apiUpdates.customer_email = updates.customer.email;
        apiUpdates.customer_phone = updates.customer.phone;
        apiUpdates.customer_address = updates.customer.address;
      }

      if (updates.items && updates.items[0]) {
        const item = updates.items[0];
        apiUpdates.duration_months = item.duration;
        apiUpdates.start_date = item.startDate.toISOString().split('T')[0];
        apiUpdates.end_date = item.endDate.toISOString().split('T')[0];
      }

      // Bổ sung truyền payment_method nếu có
      if ((updates as any).payment_method) {
        (apiUpdates as any).payment_method = (updates as any).payment_method;
      }

      // Call API to update order
      const apiOrder = await orderApi.updateOrder(orderId, apiUpdates);
      const updatedOrder = convertOrderResponseToOrder(apiOrder);

      // Merge with local updates (like paymentMethod which might not be in API response)
      if (updates.paymentMethod) {
        updatedOrder.paymentMethod = updates.paymentMethod;
      }

      setCurrentOrder(updatedOrder);
    } catch (err: any) {
      const errorMessage = err.message || 'Không thể cập nhật đơn hàng. Vui lòng thử lại.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const cancelOrder = useCallback(async (orderId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Call API to cancel order
      await orderApi.cancelOrder(orderId);
      
      // Clear current order
      setCurrentOrder(null);
      
      // Navigate back to home
      navigate('/');
    } catch (err: any) {
      const errorMessage = err.message || 'Không thể hủy đơn hàng. Vui lòng thử lại.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const selectPaymentMethod = useCallback((paymentMethod: PaymentMethod) => {
    console.log('💳 [SELECT_PAYMENT] Setting payment method:', paymentMethod.id);
    
    // Save to localStorage immediately (synchronous) as backup
    localStorage.setItem('selected_payment_method', JSON.stringify(paymentMethod));
    
    setCurrentOrder(prev => {
      if (!prev) {
        console.warn('⚠️ [SELECT_PAYMENT] currentOrder is null, cannot set payment method');
        return prev;
      }
      console.log('💳 [SELECT_PAYMENT] Updated currentOrder with payment method');
      return { ...prev, paymentMethod, updatedAt: new Date() };
    });
  }, []);

  const processPayment = useCallback(async (orderId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate orderId
      if (!orderId) {
        throw new Error('Mã đơn hàng không hợp lệ');
      }

      console.log('🔄 [PROCESS_PAYMENT] Starting for order:', orderId);

      // Get payment method from localStorage (most reliable source)
      let userSelectedPaymentMethod = null;
      const savedMethodStr = localStorage.getItem('selected_payment_method');
      if (savedMethodStr) {
        try {
          userSelectedPaymentMethod = JSON.parse(savedMethodStr);
          console.log('🔄 [PROCESS_PAYMENT] Payment method from localStorage:', userSelectedPaymentMethod.id);
        } catch (e) {
          console.warn('⚠️ [PROCESS_PAYMENT] Failed to parse saved payment method');
        }
      }

      // Fallback: Try to get from currentOrder state
      if (!userSelectedPaymentMethod && currentOrder?.paymentMethod) {
        userSelectedPaymentMethod = currentOrder.paymentMethod;
        console.log('🔄 [PROCESS_PAYMENT] Payment method from currentOrder state:', userSelectedPaymentMethod.id);
      }
      
      if (!userSelectedPaymentMethod) {
        console.error('❌ [PROCESS_PAYMENT] No payment method found');
        throw new Error('Vui lòng chọn phương thức thanh toán');
      }

      // Load fresh order data from API to get latest amounts/status ONLY
      const freshApiOrder = await orderApi.getOrder(orderId);
      const freshOrder = convertOrderResponseToOrder(freshApiOrder);
      
      console.log('🔄 [PROCESS_PAYMENT] Fresh order from API:', {
        id: freshOrder.id,
        status: freshOrder.status,
        totalAmount: freshOrder.totalAmount,
      });

      // Use total_amount from API (already calculated correctly: first_period + deposit + platform_fee)
      const paymentAmount = (freshOrder as any).total_amount || freshOrder.totalAmount;

      console.log('🔄 [PROCESS_PAYMENT] Ready to process:', {
        orderId,
        status: freshOrder.status,
        paymentMethod: userSelectedPaymentMethod.id,
        amount: paymentAmount,
      });

      // Check if payment method is wallet - wallet feature has been removed
      if (userSelectedPaymentMethod.id === 'wallet' || userSelectedPaymentMethod.id === 'internal_wallet') {
        // Wallet payment is no longer supported
        const errorMsg = 'Tính năng thanh toán bằng ví nội bộ đã bị gỡ bỏ. Vui lòng chọn phương thức thanh toán khác.';
        console.error('❌ Wallet payment not supported:', errorMsg);
        throw new Error(errorMsg);
      } else {
        // Use payment transaction API
        console.log('🔄 Processing payment transaction with method:', userSelectedPaymentMethod.id);
        await orderApi.createPaymentTransaction({
          order_id: parseInt(orderId),
          payment_method: userSelectedPaymentMethod.id,
          amount: paymentAmount,
        });

        // Reload order to get updated status
        const updatedApiOrder = await orderApi.getOrder(orderId);
        const updatedOrder = convertOrderResponseToOrder(updatedApiOrder);
        setCurrentOrder(updatedOrder);
        
        // Clear payment method from localStorage after successful payment
        localStorage.removeItem('selected_payment_method');
        
        // Add to order history
        setOrderHistory(prev => [updatedOrder, ...prev]);
        
        console.log('✅ Payment transaction completed');
        return true;
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Thanh toán thất bại. Vui lòng thử lại.';
      console.error('❌ [PROCESS_PAYMENT] Error:', errorMessage);
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentOrder]);

  const clearCurrentOrder = useCallback(() => {
    setCurrentOrder(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    currentOrder,
    orderHistory,
    isLoading,
    error,
    createOrder,
    loadOrder,
    updateOrder,
    cancelOrder,
    selectPaymentMethod,
    processPayment,
    clearCurrentOrder,
    clearError,
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export default OrderProvider;
